import { useSelector } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { PlainTooltip } from "@similarweb/ui-components";

import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import GainAccessBlock from "app/pages/sales-intelligence/common-components/GainAccessBlock/GainAccessBlock";
import { StyledListSectionName } from "app/pages/sales-intelligence/modules/lists/components/styles";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    selectCrmListParams,
    selectSortedCRMs,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmListSelectors";
import { selectHsIntegrationCrmStatus } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import { selectSfIntegrationCrmStatus } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { CrmIntegrationListStatus } from "app/pages/sales-intelligence/sub-modules/management/types";
import { handleGainAccessRedirect } from "app/common/utils/handleGainAccessRedirect";

import { InactiveCrmIntegration } from "../../components/CrmIntegration/InactiveCrmIntegration";

import { statusToViewMap } from "./statusToViewMap";
import {
    StyledCRMListsSectionBody,
    StyledListsSection,
    StyledListsSectionHeader,
    StyledTooltipContent,
} from "./styles";

const actionRequiredStatuses = [CrmIntegrationListStatus.UnmatchedEmail, CrmIntegrationListStatus.NotActive];

type CrmIntegrationContainerProps = {
    hasTitle?: boolean;
};

export const CrmIntegrationContainer = ({ hasTitle = true }: CrmIntegrationContainerProps) => {
    const translate = useTranslation();
    const hsStatus = useSelector(selectHsIntegrationCrmStatus);
    const sfStatus = useSelector(selectSfIntegrationCrmStatus);
    const crmListParams = useSelector(selectCrmListParams);
    const sortedCRMs = useSelector(selectSortedCRMs);
    const isCrmIntegrationsEnabled = salesSettingsHelper.isCrmIntegrationsEnabled();

    const renderCrmListItems = (crm: Integration) => {
        const { status } = crmListParams[crm];
        const CrmListItemView = statusToViewMap[status];

        if (actionRequiredStatuses.includes(status)) {
            return <CrmListItemView crmSyncStatusName={crm} />;
        }
    };

    const renderContent = () => {
        const syncStatuses = [hsStatus, sfStatus];

        if (syncStatuses.every((s) => s === CrmIntegrationListStatus.NotIntegrated)) {
            const crmSyncStatusName = hasTitle ? "notIntegratedCrmWithOutTitle" : "notIntegratedCrm";

            return <InactiveCrmIntegration crmSyncStatusName={crmSyncStatusName} />;
        }

        if (syncStatuses.every((s) => s === CrmIntegrationListStatus.NotActive)) {
            const crmSyncStatusName = hasTitle ? "notActiveCrmWithOutTitle" : "notActiveCrm";

            return <InactiveCrmIntegration crmSyncStatusName={crmSyncStatusName} />;
        }

        if (hsStatus === CrmIntegrationListStatus.NotIntegrated) {
            return renderCrmListItems("salesforce");
        }

        if (sfStatus === CrmIntegrationListStatus.NotIntegrated) {
            return renderCrmListItems("hubspot");
        }

        return sortedCRMs.map((crm) => renderCrmListItems(crm));
    };

    return (
        <StyledListsSection data-automation="si-crm-lists-section">
            {hasTitle ? (
                <StyledListsSectionHeader>
                    <StyledListSectionName>{translate("si.crm-integration.title")}</StyledListSectionName>
                    <PlainTooltip tooltipContent={translate("si.common.crm-integration.title.tooltip")}>
                        <StyledTooltipContent>
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </StyledTooltipContent>
                    </PlainTooltip>
                </StyledListsSectionHeader>
            ) : null}
            {!isCrmIntegrationsEnabled ? (
                <GainAccessBlock
                    title="si.crm-list.hook.get-access.title"
                    subTitle="si.crm-list.hook.get-access.subtitle"
                    imageName={"gain-access-crm-lock.svg"}
                    isExploreOurPackages={true}
                    handleGainAccessRedirect={() =>
                        handleGainAccessRedirect(
                            "go to package page",
                            "click",
                            "Internal Link",
                            "My CRM Lists",
                            "",
                            "MyCRMLists",
                        )
                    }
                />
            ) : (
                <StyledCRMListsSectionBody>{renderContent()}</StyledCRMListsSectionBody>
            )}
        </StyledListsSection>
    );
};
